import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import i18n from "@/i18n/config";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Smart date formatter: shows "Today" / "Yesterday" when applicable,
 * otherwise a localized date. When `withTime` is true, time is appended.
 */
export function formatSmartDate(
  value: string | number | Date | null | undefined,
  opts: { withTime?: boolean } = {}
): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";

  const locale = i18n.language?.startsWith("ar") ? "ar-EG" : undefined;
  const today = startOfDay(new Date());
  const target = startOfDay(d);
  const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);

  let datePart: string;
  if (diffDays === 0) datePart = i18n.t("common.today", { defaultValue: "Today" });
  else if (diffDays === 1) datePart = i18n.t("common.yesterday", { defaultValue: "Yesterday" });
  else datePart = d.toLocaleDateString(locale);

  if (!opts.withTime) return datePart;
  const timePart = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `${datePart} ${timePart}`;
}
